import { supabase } from '../../config/supabase';

/**
 * Create a new job
 * @param {Object} jobData - Job data
 * @param {string} companyId - Company ID
 * @param {string} createdByUserId - User ID who created the job
 * @param {string} createdByName - Name of user who created the job
 * @returns {Promise<Object>} Created job
 */
export const createJob = async (jobData, companyId, createdByUserId, createdByName) => {
    try {
        // 1. Create the main job
        const { data: job, error: jobError } = await supabase
            .from('jobs')
            .insert({
                company_id: companyId,
                unique_id: jobData.uniqueId,
                customer_name: jobData.customerName,
                customer_number: jobData.customerNumber ? `+61${jobData.customerNumber}` : null,
                customer_mail: jobData.customerMail || null,
                carpenter_supervisor_name: jobData.carpenterSupervisorName || null,
                carpenter_supervisor_number: jobData.carpenterSupervisorNumber ? `+61${jobData.carpenterSupervisorNumber}` : null,
                customer_address: jobData.customerAddress || null,
                job_type: jobData.jobType,
                main_status: jobData.jobType === 'Timber' ? 'ready_for_delivery' :
                    jobData.jobType === 'Estimation' ? 'pending_estimation' : 'pending_design',
                invoiced: jobData.invoiced || false,
                booking_delivery_date: jobData.bookingDeliveryDate || null,
                expected_estimation_finish_date: jobData.estimationFinishDate || null,
                roof_type: jobData.jobInformation?.roofType || null,
                roof_truss_centres: jobData.jobInformation?.roofTrussCentres || null,
                is_single_storey: jobData.jobInformation?.isSingleStorey ?? true,
                is_unit: jobData.jobInformation?.isUnit || false,
                unit_count: jobData.jobInformation?.unitCount || null,
                urgent_quote: jobData.jobInformation?.urgentQuote || false,
                urgent_design: jobData.jobInformation?.urgentDesign || false,
                site_measure_required: jobData.jobInformation?.siteMeasure?.required || false,
                site_measure_wall: jobData.jobInformation?.siteMeasure?.wall || false,
                site_measure_floor: jobData.jobInformation?.siteMeasure?.floor || false,
                site_measure_truss: jobData.jobInformation?.siteMeasure?.truss || false,
                created_by_user_id: createdByUserId,
                created_by_name: createdByName
            })
            .select()
            .single();

        if (jobError) throw jobError;

        // 2. Create job quotes (roof, wall, floor, timber)
        const quotesToInsert = [];
        Object.entries(jobData.quotes).forEach(([quoteType, quoteData]) => {
            if (quoteData.isRequired) {
                quotesToInsert.push({
                    job_id: job.id,
                    quote_type: quoteType,
                    is_required: true,
                    design_status: jobData.jobType === 'Estimation' ? 'not_required' : 'waiting_assignment',
                    estimation_status: jobData.jobType === 'Design' ? 'not_required' : 'waiting_design',
                    expected_finish_date: quoteData.expectedFinishDate || null
                });
            }
        });

        if (quotesToInsert.length > 0) {
            const { error: quotesError } = await supabase
                .from('job_quotes')
                .insert(quotesToInsert);

            if (quotesError) throw quotesError;
        }

        // 3. Create floor specifications if wall is required
        if (jobData.quotes.wall?.isRequired) {
            const floorSpecsToInsert = [];

            if (jobData.jobInformation.isSingleStorey) {
                floorSpecsToInsert.push({
                    job_id: job.id,
                    floor_level: 'single',
                    load_bearing_wall_stud_sizes: jobData.jobInformation.singleStorey?.loadBearingWallStudSizes || '90x45',
                    non_load_bearing_wall_stud_sizes: jobData.jobInformation.singleStorey?.nonLoadBearingWallStudSizes || null,
                    non_load_bearing_wall_stud_centres: jobData.jobInformation.singleStorey?.nonLoadBearingWallStudCentres || null,
                    wall_height: jobData.jobInformation.singleStorey?.wallHeight || null
                });
            } else {
                // Ground floor
                floorSpecsToInsert.push({
                    job_id: job.id,
                    floor_level: 'ground',
                    load_bearing_wall_stud_sizes: jobData.jobInformation.doubleStorey?.groundFloor?.loadBearingWallStudSizes || '90x45',
                    non_load_bearing_wall_stud_sizes: jobData.jobInformation.doubleStorey?.groundFloor?.nonLoadBearingWallStudSizes || null,
                    non_load_bearing_wall_stud_centres: jobData.jobInformation.doubleStorey?.groundFloor?.nonLoadBearingWallStudCentres || null,
                    wall_height: jobData.jobInformation.doubleStorey?.groundFloor?.wallHeight || null
                });

                // First floor
                floorSpecsToInsert.push({
                    job_id: job.id,
                    floor_level: 'first',
                    load_bearing_wall_stud_sizes: jobData.jobInformation.doubleStorey?.firstFloor?.loadBearingWallStudSizes || '90x45',
                    non_load_bearing_wall_stud_sizes: jobData.jobInformation.doubleStorey?.firstFloor?.nonLoadBearingWallStudSizes || null,
                    non_load_bearing_wall_stud_centres: jobData.jobInformation.doubleStorey?.firstFloor?.nonLoadBearingWallStudCentres || null,
                    wall_height: jobData.jobInformation.doubleStorey?.firstFloor?.wallHeight || null
                });
            }

            const { error: specsError } = await supabase
                .from('job_floor_specs')
                .insert(floorSpecsToInsert);

            if (specsError) throw specsError;
        }

        // 4. Create floors if floor quote is required
        if (jobData.quotes.floor?.isRequired && jobData.jobInformation.floors?.length > 0) {
            const floorsToInsert = jobData.jobInformation.floors.map(floor => ({
                job_id: job.id,
                floor_number: floor.floorNumber,
                floor_truss_size: floor.floorTrussSize || null,
                flooring: floor.flooring || null,
                balcony_included: floor.balconyIncluded || false
            }));

            const { error: floorsError } = await supabase
                .from('job_floors')
                .insert(floorsToInsert);

            if (floorsError) throw floorsError;
        }

        // 5. Create notes
        if (jobData.jobInformation.notes?.length > 0) {
            const notesToInsert = jobData.jobInformation.notes.map(note => ({
                job_id: job.id,
                content: note.content,
                added_by_user_id: createdByUserId,
                added_by_name: createdByName,
                added_by_role: 'admin', // or get from user profile
                added_at: note.addedAt || new Date().toISOString()
            }));

            const { error: notesError } = await supabase
                .from('job_notes')
                .insert(notesToInsert);

            if (notesError) throw notesError;
        }

        // 6. Create action log
        const { error: logError } = await supabase
            .from('job_action_logs')
            .insert({
                job_id: job.id,
                user_id: createdByUserId,
                user_name: createdByName,
                user_role: 'admin',
                action: 'job_created',
                details: `Job ${job.unique_id} created`
            });

        if (logError) throw logError;

        return { job, message: 'Job created successfully' };
    } catch (error) {
        console.error('Error creating job:', error);
        throw error;
    }
};

/**
 * Get customers for a company
 * @param {string} companyId - Company ID
 * @returns {Promise<Array>} List of customers
 */
export const getCustomers = async (companyId) => {
    try {
        // Note: You'll need to create a customers table or get from users
        // For now, returning empty array - implement based on your needs
        return [];
    } catch (error) {
        console.error('Error fetching customers:', error);
        throw error;
    }
};

/**
 * Add a new customer
 * @param {Object} customerData - Customer data
 * @param {string} companyId - Company ID
 * @returns {Promise<Object>} Created customer
 */
export const addCustomer = async (customerData, companyId) => {
    try {
        // Implement customer creation if you have a customers table
        // For now, returning success message
        return { message: 'Customer added successfully', customer: customerData };
    } catch (error) {
        console.error('Error adding customer:', error);
        throw error;
    }
};

/**
 * Upload files for a job
 * @param {string} jobId - Job ID
 * @param {Array} files - Array of files to upload
 * @param {string} uploadedByUserId - User ID who uploaded
 * @param {string} uploadedByName - Name of uploader
 * @returns {Promise<Array>} Uploaded file records
 */
export const uploadJobFiles = async (jobId, files, uploadedByUserId, uploadedByName, companySlug) => {
    try {
        const uploadedFiles = [];

        for (const file of files) {
            // 1. Upload file to Supabase Storage
            const fileName = `${Date.now()}-${file.name}`;
            const { data: uploadData, error: uploadError } = await supabase.storage
                .from('job-files') // You need to create this bucket in Supabase
                .upload(`${companySlug}/${jobId}/${fileName}`, file);

            if (uploadError) throw uploadError;

            // 2. Get public URL
            const { data: { publicUrl } } = supabase.storage
                .from('job-files')
                .getPublicUrl(`${companySlug}/${jobId}/${fileName}`);

            // 3. Create file record in database
            const { data: fileRecord, error: recordError } = await supabase
                .from('job_files')
                .insert({
                    job_id: jobId,
                    filename: fileName,
                    original_name: file.name,
                    url: publicUrl,
                    uploaded_by_user_id: uploadedByUserId,
                    uploaded_by_name: uploadedByName,
                    uploaded_by_role: 'admin',
                    file_type: 'initial'
                })
                .select()
                .single();

            if (recordError) throw recordError;

            uploadedFiles.push(fileRecord);
        }

        return uploadedFiles;
    } catch (error) {
        console.error('Error uploading files:', error);
        throw error;
    }
};


/**
 * Fetch all jobs/assignments for a company
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of job assignments
 */
export const getAssignments = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(*),
                job_designers(*),
                job_estimators(*),
                job_production(*),
                job_deliveries(*)
            `)
            .eq('company_id', companyId)
            .eq('deleted', false)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error('Error fetching assignments:', error);
        throw error;
    }
};

/**
 * Subscribe to real-time job updates
 * @param {string} companyId - The company ID
 * @param {Function} callback - Callback function for updates
 * @returns {Object} Supabase channel subscription
 */
export const subscribeToJobUpdates = (companyId, callback) => {
    const channel = supabase
        .channel(`jobs-changes-${companyId}`)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'jobs',
                filter: `company_id=eq.${companyId}`
            },
            callback
        )
        .subscribe();

    return channel;
};

/**
 * Unsubscribe from job updates
 */
export const unsubscribeFromJobUpdates = async (channel) => {
    if (channel) {
        await supabase.removeChannel(channel);
    }
};

/**
 * Get required quote types for a job
 * @param {Object} job - Job object with job_quotes
 * @returns {Array<string>} Array of required quote types
 */
export const getRequiredQuotes = (job) => {
    const quotes = [];
    job.job_quotes?.forEach(quote => {
        if (quote.is_required) {
            quotes.push(quote.quote_type);
        }
    });
    return quotes;
};

/**
 * Transform job data from snake_case to camelCase
 */
const transformJobData = (job) => {
    // Transform quotes
    const quotes = {};
    if (job.job_quotes) {
        job.job_quotes.forEach(quote => {
            // Get designers for this quote type
            const designers = job.job_designers
                ?.filter(d => d.quote_type === quote.quote_type)
                ?.map(d => ({
                    userId: d.user_id,
                    name: d.name,
                    isStarted: d.is_started,
                    isCompleted: d.is_completed
                })) || [];

            quotes[quote.quote_type] = {
                isRequired: quote.is_required,
                designStatus: quote.design_status,
                estimationStatus: quote.estimation_status,
                designers: designers,
                production: job.job_production?.find(p => p.quote_type === quote.quote_type) ? {
                    phase: job.job_production.find(p => p.quote_type === quote.quote_type).phase,
                    userId: job.job_production.find(p => p.quote_type === quote.quote_type).user_id,
                    name: job.job_production.find(p => p.quote_type === quote.quote_type).name,
                    isStarted: job.job_production.find(p => p.quote_type === quote.quote_type).is_started_production,
                    isCompleted: job.job_production.find(p => p.quote_type === quote.quote_type).is_completed,
                    startCuttingAt: job.job_production.find(p => p.quote_type === quote.quote_type).start_cutting_at,
                    endCuttingAt: job.job_production.find(p => p.quote_type === quote.quote_type).end_cutting_at,
                    startProductionAt: job.job_production.find(p => p.quote_type === quote.quote_type).start_production_at,
                    endProductionAt: job.job_production.find(p => p.quote_type === quote.quote_type).end_production_at,
                } : null,
                delivery: job.job_deliveries?.find(d => d.quote_type === quote.quote_type) ? {
                    isRequired: job.job_deliveries.find(d => d.quote_type === quote.quote_type).is_required,
                    deliveryStatus: job.job_deliveries.find(d => d.quote_type === quote.quote_type).delivery_status,
                    deliveryDate: job.job_deliveries.find(d => d.quote_type === quote.quote_type).delivery_date,
                    assignedDriver: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_driver_id ? {
                        driverId: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_driver_id,
                        driverName: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_driver_name,
                        assignedAt: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_at,
                        assignedBy: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_by_name ? {
                            name: job.job_deliveries.find(d => d.quote_type === quote.quote_type).assigned_by_name
                        } : null
                    } : null,
                    deliveryNotes: job.job_deliveries.find(d => d.quote_type === quote.quote_type).delivery_notes
                } : null
            };
        });
    }

    // Transform estimator
    const estimator = job.job_estimators?.[0] ? {
        userId: job.job_estimators[0].user_id,
        name: job.job_estimators[0].name,
        isCompleted: job.job_estimators[0].is_completed,
        isStarted: job.job_estimators[0].is_started
    } : null;

    return {
        _id: job.id,
        uniqueId: job.unique_id,
        customerName: job.customer_name,
        customerNumber: job.customer_number,
        customerMail: job.customer_mail,
        carpenterSupervisorName: job.carpenter_supervisor_name,
        customerAddress: job.customer_address,
        jobType: job.job_type,
        mainStatus: job.main_status,
        currentStatus: job.current_status,
        estimationFinishDate: job.expected_estimation_finish_date,
        bookingDeliveryDate: job.booking_delivery_date,
        invoiced: job.invoiced,
        jobInformation: {
            urgentQuote: job.urgent_quote,
            urgentDesign: job.urgent_design
        },
        quotes: quotes,
        estimator: estimator,
        createdAt: job.created_at,
        updatedAt: job.updated_at
    };
};

/**
 * Search jobs with text query
 */
export const searchJobs = async (companyId, filters = {}) => {
    try {
        const { searchQuery, userId } = filters;

        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(*),
                job_designers(*),
                job_estimators(*),
                job_production(*),
                job_deliveries(*)
            `)
            .eq('company_id', companyId)
            .eq('deleted', false);

        // Apply text search
        if (searchQuery) {
            query = query.or(`unique_id.ilike.%${searchQuery}%,customer_name.ilike.%${searchQuery}%,carpenter_supervisor_name.ilike.%${searchQuery}%`);
        }

        // Apply user filter
        if (userId) {
            const { data: designerJobs } = await supabase
                .from('job_designers')
                .select('job_id')
                .eq('user_id', userId);

            const { data: estimatorJobs } = await supabase
                .from('job_estimators')
                .select('job_id')
                .eq('user_id', userId);

            const jobIds = [
                ...(designerJobs?.map(d => d.job_id) || []),
                ...(estimatorJobs?.map(e => e.job_id) || [])
            ];

            if (jobIds.length > 0) {
                query = query.in('id', jobIds);
            }
        }

        const { data, error } = await query.limit(50);

        if (error) throw error;

        return data?.map(transformJobData) || [];
    } catch (error) {
        console.error('Error searching jobs:', error);
        throw error;
    }
};

/**
 * Assign a designer to a job quote
 */
export const assignDesignerToJob = async (jobId, quoteType, designerId) => {
    try {
        // Check if designer assignment already exists
        const { data: existing } = await supabase
            .from('job_designers')
            .select('*')
            .eq('job_id', jobId)
            .eq('quote_type', quoteType)
            .eq('user_id', designerId)
            .single();

        if (existing) {
            return existing;
        }

        // Get designer info
        const { data: designer } = await supabase
            .from('users')
            .select('username, full_name')
            .eq('id', designerId)
            .single();

        // Insert new designer assignment
        const { data, error } = await supabase
            .from('job_designers')
            .insert({
                job_id: jobId,
                quote_type: quoteType,
                user_id: designerId,
                name: designer?.username || designer?.full_name,
                assigned_at: new Date().toISOString(),
                design_status: "in_progress"
            })
            .select()
            .single();

        if (error) throw error;

        return data;
    } catch (error) {
        console.error('Error assigning designer:', error);
        throw error;
    }
};